import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import urlJoin from 'url-join';
import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import {
  ProcessOneDataType,
  PurchaseFormValuesType,
} from 'QuoteApp/screens/Purchase';
import {
  PaymentTokenResponseType,
  transformLegacyPaymentTokenResponse,
  transformProcessOnePaymentTokenRequest,
} from 'QuoteApp/services/endpoints/PostLegacyPaymentToken';
import {
  PurchaseProviderType,
  useAppContext,
} from 'QuoteApp/services/providers';
import {
  getErrorMessage,
  getErrorStatusCode,
  runWithPolling,
  TIMEOUT_ERROR,
} from 'QuoteApp/utils';
import { CongratsAgentHistoryLocationState } from 'QuoteApp/screens/CongratsAgent';

type BindStatusResponse = {
  status: string;
  decision?: string;
  policyNumber?: string;
  policyId?: string;
  errors?: Array<{
    message: string;
    errorCode?: string;
  }>;
};

export type PurchaseSubmitType = {
  appDataStale: boolean;
  errors: Array<string> | null;
  clearErrors(): void;
  processOneBind: (
    data: ProcessOneDataType,
    formValues: PurchaseFormValuesType
  ) => Promise<void>;
  submitBind: (
    values: PurchaseFormValuesType,
    paymentToken: string
  ) => Promise<void>;
  processing: boolean;
  setErrors(errors: Array<string>): void;
};

export const usePurchaseSubmit = (
  appId: string,
  onPaymentError: PurchaseProviderType['onPaymentError']
): PurchaseSubmitType => {
  const [bindInProgress, setBindInProgress] = useState<boolean>(false);
  const [appDataStale, setAppDataStale] = useState<boolean>(false);
  const [errors, setErrors] = useState<Array<string> | null>(null);
  const { toast } = useToast();
  const history = useHistory();
  const {
    details,
    refreshDetails,
    fetchRate,
    rate,
    rewardsEnrollmentResponse,
    isEstimatePointsLoading,
    submitEstimatePoints,
  } = useAppContext();

  const [{ loading: submittingBindRequest }, submitBindRequest] =
    useLazyQuoteApi<{
      bindAttemptId: string;
    }>({
      method: 'POST',
      url: `insurance_applications/${appId}/bind`,
    });

  const [
    { loading: submittingProcessOnePaymentTokenRequest },
    submitProcessOnePaymentTokenRequest,
  ] = useLazyQuoteApi<PaymentTokenResponseType>({
    method: 'POST',
    url: `insurance_applications/${appId}/payment_tokens`,
  });

  const [, getStatus] = useLazyQuoteApi<BindStatusResponse>({});

  const showToastError = useCallback(
    (message: string) => {
      toast({
        type: 'error',
        message: 'An error occurred while processing payment',
        context: { message },
        icon: 'error',
        withSecondaryNav: false,
      });
    },
    [toast]
  );

  const handleBindResponse = useCallback(
    async (response: BindStatusResponse) => {
      if (
        ['payment_error', 'matching_policy_error', 'error'].includes(
          response.status
        )
      ) {
        setBindInProgress(false);
        const bindErrorMessages =
          response.errors?.map((error) => error.message) || null;
        if (bindErrorMessages) {
          setErrors(bindErrorMessages);
          onPaymentError();
        }
        showToastError(
          bindErrorMessages?.join(', ') || response.decision || response.status
        );
        return;
      }
      let rewardPoints: Awaited<
        ReturnType<typeof submitEstimatePoints>
      > | null = null;

      if (rewardsEnrollmentResponse?.isEnrolled && details) {
        rewardPoints = await submitEstimatePoints(details);
      }

      // Refresh details to update app status and handle bound status or potential UW stop
      await refreshDetails();

      if (
        response.status === 'completed' &&
        response.decision === 'policy_bound' &&
        response.policyNumber
      ) {
        let locationState: CongratsAgentHistoryLocationState;

        if (rewardsEnrollmentResponse?.isEnrolled) {
          locationState =
            rewardPoints !== null
              ? {
                  pointsEarned: rewardPoints.totalPoints,
                  didEstimatePointsFail: false,
                  policyId: response.policyId,
                }
              : {
                  pointsEarned: undefined,
                  didEstimatePointsFail: true,
                  policyId: response.policyId,
                };
        } else {
          locationState = {
            policyId: response.policyId,
          };
        }

        history.push(
          urlJoin(QUOTE_APP_ROUTE, appId, 'congrats', response.policyNumber),
          locationState
        );
      }
    },
    [
      rewardsEnrollmentResponse?.isEnrolled,
      refreshDetails,
      showToastError,
      onPaymentError,
      details,
      submitEstimatePoints,
      history,
      appId,
    ]
  );

  const handleStaleAppData = useCallback(async () => {
    setAppDataStale(true);
    try {
      // Call rate first since it can affect commands
      const newRate = await fetchRate();

      let rateChangeMessage;
      if (rate && newRate && rate.totalPremium !== newRate.totalPremium) {
        rateChangeMessage = `The rate changed from ${rate.totalPremium} to ${newRate.totalPremium}.`;
      }

      await refreshDetails();

      history.push(urlJoin(QUOTE_APP_ROUTE, appId, 'summary'), {
        appDataStale: true,
        rateChangeMessage: rateChangeMessage,
      });
    } catch (e) {
      setAppDataStale(false);
      showToastError(getErrorMessage(e));
    }
  }, [appId, fetchRate, history, rate, refreshDetails, showToastError]);

  const submitBind = useCallback(
    async (values: PurchaseFormValuesType, paymentToken: string) => {
      const data = {
        payment_method: values.paymentMethod,
        payment_plan_id: values.paymentPlanId,
        payment_token: paymentToken,
        quote_id: values.quoteId,
      };
      setErrors(null);
      const bindResponse = await submitBindRequest({ data }).catch(
        async (e) => {
          if (getErrorStatusCode(e) === 409) {
            handleStaleAppData();
          } else {
            showToastError(getErrorMessage(e));
          }
        }
      );

      if (bindResponse) {
        setBindInProgress(true);
        await runWithPolling<BindStatusResponse>(
          async () => {
            const response = await getStatus({
              url: `insurance_applications/${appId}/bind_attempts/${bindResponse.data.bindAttemptId}/status`,
            });
            return response.data;
          },
          (statusResponse) => statusResponse.status !== 'pending'
        )
          .then(async (response) => {
            await handleBindResponse(response);
          })
          .catch((err) => {
            // bind polling timed out, or other error occurred
            setBindInProgress(false);
            showToastError(err === TIMEOUT_ERROR ? 'timeout' : 'error');
          });
      }
    },
    [
      appId,
      getStatus,
      handleBindResponse,
      handleStaleAppData,
      showToastError,
      submitBindRequest,
    ]
  );

  const processOneBind = useCallback(
    async (data: ProcessOneDataType, formValues: PurchaseFormValuesType) => {
      setErrors(null);
      try {
        const response = await submitProcessOnePaymentTokenRequest({
          data: {
            ...data,
            entityId: appId,
          },
          transformRequest: transformProcessOnePaymentTokenRequest,
          transformResponse: transformLegacyPaymentTokenResponse,
        });
        if (response.data.paymentToken) {
          await submitBind(formValues, response.data.paymentToken);
        } else if (response.data.errors) {
          setErrors(response.data.errors);
          showToastError(response.data.errors.join(', '));
        }
      } catch (e) {
        showToastError(getErrorMessage(e));
      }
    },
    [appId, showToastError, submitBind, submitProcessOnePaymentTokenRequest]
  );

  return {
    appDataStale,
    errors,
    clearErrors: () => setErrors(null),
    processOneBind,
    submitBind,
    setErrors,
    processing:
      submittingProcessOnePaymentTokenRequest ||
      submittingBindRequest ||
      bindInProgress ||
      (isEstimatePointsLoading && bindInProgress),
  };
};
